import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Footer from '../../../components/layout/Footer';
import AD_LANDING_CONFIG, { COMMON_SOCIAL_PROOF, COMMON_FEATURES, getPricePlans } from './adLandingConfig';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getMetaMatchParams } from '../../../utils/analytics';
import { useToast } from '../../../components/Toast';
import { getSeasonForAngle, getDeadlineMs, getNewYearPrices, getDiscountRateLabel, isAfterCutover } from '../../../config/seasonConfig';
import { PRO50_PRICES, getPro50DiscountPercent, isPro50Active } from '../../../utils/pro50Campaign';
import '../PreRegisterLanding.css';
import '../../features/FeatureLanding.css';
import '../ad-pro/AdProLandingPage.css';
import './AdLandingPage.css';
import { CLASS_PHOTO_STYLE_PRESETS } from '../../../config/classPhotoEventConfig';
import {
  ClassFlowDemo,
  ImageResultDemo,
  PptEditDemo,
  SaenggibuResultDemo,
  ScoringScanDemo,
  trackDemoCta,
} from '../../../components/demo';

const _won = (n) => `${n.toLocaleString()}원`;

// 10월 PRO 최대 50% 이벤트 중에는 표의 가격(PRO50_PRICES)과 같은 숫자로 바꾼다.
const _applyPro50ToCopy = (str) => {
  const price12 = PRO50_PRICES['12month'];
  const rate12 = `${getPro50DiscountPercent('12month')}%`;
  return str
    .replace(/시즌 특가 140,000원/g, `10월 특가 ${_won(price12)}`)
    .replace(/140,000원/g, _won(price12))
    .replace(/82,500원/g, _won(PRO50_PRICES['6month']))
    .replace(/51,000원/g, _won(PRO50_PRICES['3month']))
    .replace(/하루 383원/g, `하루 ${_won(Math.round(price12 / 365))}`)
    .replace(/월 11,667원/g, `월 ${_won(Math.round(price12 / 12))}`)
    .replace(/200,000원의 30%/g, `200,000원의 ${rate12}`)
    .replace(/최대 30% 할인/g, '최대 50% 할인')
    .replace(/시즌 특가 30%/g, '10월 특가 최대 50%')
    .replace(/30% 할인/g, `${rate12} 할인`);
};

// angle config 카피는 6/1 이후 신가(30%/140k/383원) 기준으로 적는다.
// 10월 이벤트 중에는 이벤트가로, 5/31까지는 50%/99k/270원으로 자동 swap
export const applySeasonPriceCopy = (str, now = new Date()) => {
  if (!str || typeof str !== 'string') return str;
  if (isPro50Active(now)) return _applyPro50ToCopy(str);
  if (isAfterCutover(now)) return str; // 6/1 이후 = 신가 카피 그대로
  // 5/31까지 = 구가 카피로 swap
  return str
    .replace(/시즌 특가 140,000원/g, '사전예약 99,000원')
    .replace(/140,000원/g, '99,000원')
    .replace(/82,500원/g, '69,000원')
    .replace(/51,000원/g, '49,000원')
    .replace(/하루 383원/g, '하루 270원')
    .replace(/12개월 140,000원/g, '12개월 99,000원')
    .replace(/200,000원의 30%/g, '200,000원의 50%')
    .replace(/30%의 30%/g, '30%의 50%') // 안전망: 위 치환 후 잔존 케이스
    .replace(/시즌 한정 최대 30% 할인/g, '시즌 한정 최대 50% 할인')
    .replace(/최대 30% 할인/g, '최대 50% 할인')
    .replace(/시즌 특가 30%/g, '시즌 특가 50%')
    .replace(/30% 할인/g, '50% 할인');
};

// angle config 객체에 시즌 가격 카피 적용 (hero/cta/urgencyText 자동 swap)
export const applySeasonPriceConfig = (config, now = new Date()) => {
  if (!config) return config;
  const pro50 = isPro50Active(now);
  if (!pro50 && isAfterCutover(now)) return config;
  const copy = (str) => applySeasonPriceCopy(str, now);
  const priceParam = pro50 ? `price=${PRO50_PRICES['12month']}` : 'price=99000';
  return {
    ...config,
    hero: config.hero ? {
      ...config.hero,
      headline: copy(config.hero.headline),
      highlight: copy(config.hero.highlight),
      sub: copy(config.hero.sub),
      badge: copy(config.hero.badge),
    } : config.hero,
    cta: config.cta ? {
      ...config.cta,
      text: copy(config.cta.text),
      sub: copy(config.cta.sub),
      // dest URL의 price 파라미터도 시즌가로 (백엔드 검증과 일치)
      dest: config.cta.dest ? config.cta.dest.replace('price=140000', priceParam) : config.cta.dest,
    } : config.cta,
    urgencyText: copy(config.urgencyText),
  };
};

const PRODUCT_DEMOS = {
  saenggibu: {
    Demo: SaenggibuResultDemo,
    title: '메모 두 줄이면, 바이트까지 맞춘 생기부',
    path: '/create-saenggibu',
    cta: 'AI 생기부 무료 체험하기',
  },
  class_flow: {
    Demo: ClassFlowDemo,
    title: '반 전체 생기부, 겹치는 문장까지 한 번에',
    path: '/create-saenggibu?mode=batch',
    cta: '반 전체 생기부 만들어 보기',
  },
  ppt: {
    Demo: PptEditDemo,
    title: '만든 PPT, 글자를 눌러 바로 고쳐요',
    path: '/chat?category=ppt_maker',
    cta: 'AI PPT 무료로 만들기',
  },
  scoring: {
    Demo: ScoringScanDemo,
    title: '스캔 PDF 한 번이면 반 전체 채점',
    path: '/scoring',
    cta: 'AI 채점 무료 체험하기',
  },
  image: {
    Demo: ImageResultDemo,
    title: '말로 설명하면 수업 자료 그림 완성',
    path: '/chat?category=image_generation',
    cta: 'AI 이미지 무료로 만들기',
  },
};

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);

const FeatureIcon = ({ type }) => {
  const icons = {
    ppt: <><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>,
    worksheet: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>,
    saenggibu: <><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></>,
    scoring: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
  };
  return (
    <div className="adlp-feature-icon-wrap">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {icons[type] || icons.ppt}
      </svg>
    </div>
  );
};

const AdLandingPage = () => {
  const { adSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  // 시즌 가격 카피 자동 적용: 5/31까지 50%/99k, 6/1부터 30%/140k, 10월 이벤트 중에는 이벤트가
  // 아래 effect들이 config를 의존성으로 쓰므로 렌더마다 새 객체가 되면 픽셀 중복 발사·카운트다운 정지가 생긴다.
  const config = useMemo(() => applySeasonPriceConfig(AD_LANDING_CONFIG[adSlug]), [adSlug]);

  const [utmParams, setUtmParams] = useState({});
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [formData, setFormData] = useState({ name: '', organization: '', phone: '', email: '', selectedPlan: '12month', quantity: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(key => {
      if (params.get(key)) utm[key] = params.get(key);
    });
    if (!Object.values(utm).some(v => v)) {
      try {
        const stored = localStorage.getItem('whatquiz_utm');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (!parsed.expiresAt || Date.now() <= parsed.expiresAt) {
            ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(key => {
              if (parsed[key]) utm[key] = parsed[key];
            });
          }
        }
      } catch (_e) { /* ignore */ }
    }
    setUtmParams(utm);

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
      window.fbq('track', 'ViewContent', {
        content_name: config?.adSetName || adSlug,
        content_category: config?.template,
        content_type: 'ad_landing',
        landing_page: `/lp/${adSlug}`,
      });
    }

    if (utm.utm_source || utm.utm_content) {
      localStorage.setItem('whatquiz_utm', JSON.stringify({
        ...utm,
        expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
        landing_page: `/lp/${adSlug}`,
      }));

      try {
        const existingFirst = localStorage.getItem('whatquiz_first_utm');
        if (!existingFirst || (JSON.parse(existingFirst).expiresAt && Date.now() > JSON.parse(existingFirst).expiresAt)) {
          localStorage.setItem('whatquiz_first_utm', JSON.stringify({
            ...utm,
            landing_page: `/lp/${adSlug}`,
            referrer: document.referrer || 'direct',
            first_visit: new Date().toISOString(),
            savedAt: Date.now(),
            expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000,
          }));
        }
      } catch (_e) { /* ignore */ }
    }
  }, [location.search, adSlug, config]);

  useEffect(() => {
    if (!config) return;
    const season = getSeasonForAngle(config.seasonAngle);
    const deadlineMs = getDeadlineMs(season);
    const timer = setInterval(() => {
      const diff = deadlineMs - Date.now();
      if (diff <= 0) { clearInterval(timer); return; }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [config]);

  const handleCTA = () => {
    if (!config) return;
    const dest = config.cta.dest;
    const ctaType = dest === '#form' ? 'form_scroll' : dest.startsWith('http') ? 'external' : 'internal_nav';
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', {
        content_name: config.adSetName,
        content_category: config.template,
        value: 0,
        currency: 'KRW',
        landing_page: `/lp/${adSlug}`,
        cta_type: ctaType,
        cta_dest: dest,
        ...utmParams,
      });
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'ad_landing_cta_click', {
        ad_set: config.adSetName,
        template: config.template,
        landing_slug: adSlug,
        cta_type: ctaType,
        cta_dest: dest,
        ...utmParams,
      });
    }
    if (dest === '#form') {
      document.getElementById('adlp-form')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (dest.startsWith('http')) {
      window.location.href = dest;
    } else {
      const sep = dest.includes('?') ? '&' : '?';
      const qs = Object.entries(utmParams).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      navigate(qs ? `${dest}${sep}${qs}` : dest);
    }
  };

  if (!config) {
    return (
      <div className="feature-landing">
        <div className="adlp-empty">
          <h1>페이지를 찾을 수 없어요</h1>
          <p>올바른 링크인지 확인해주세요.</p>
          <button className="feature-hero-cta" onClick={() => navigate('/')}>홈으로 이동</button>
        </div>
        <Footer />
      </div>
    );
  }

  const seasonForPage = getSeasonForAngle(config?.seasonAngle);
  const deadlineMsForPage = getDeadlineMs(seasonForPage);
  const daysLeft = Math.max(0, Math.ceil((deadlineMsForPage - Date.now()) / (1000 * 3600 * 24)));

  // ── Shared sections ──

  const Hero = () => (
    <section className="feature-hero adlp-hero" style={{ background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)' }}>
      <div className="feature-hero-inner">
        <h1>{config.hero.headline}<br /><em>{config.hero.highlight}</em></h1>
        <p className="feature-hero-desc">
          {config.hero.sub.split('\n').map((l, i) => <React.Fragment key={i}>{l}{i < config.hero.sub.split('\n').length - 1 && <br />}</React.Fragment>)}
        </p>
        <button className="feature-hero-cta" onClick={handleCTA}>{config.cta.text}</button>
        {config.cta.sub && <p className="preregister-hero-sub">{config.cta.sub}</p>}
      </div>
    </section>
  );

  const Steps = () => {
    if (!config.steps) return null;
    return (
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">이렇게 쉬워요</h2>
          <div className="preregister-benefits">
            {config.steps.map((s, i) => (
              <div key={i} className="preregister-benefit">
                <div className="preregister-benefit-number">
                  <span className="adlp-step-num">{i + 1}</span>
                </div>
                <div className="preregister-benefit-text">
                  <h3>{s.label}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const Features = () => (
    <section className="feature-section" style={{ background: '#f8f9fa' }}>
      <div className="feature-section-inner">
        <h2 className="feature-section-title">이 모든 게 하나로</h2>
        <p className="feature-section-subtitle">GPT, Claude, Gemini 최신 AI 3종을 한 곳에서 쓸 수 있어요</p>
        <div className="preregister-benefits">
          {COMMON_FEATURES.map((f) => (
            <div key={f.key} className={`preregister-benefit ${config.focusFeature === f.key ? 'adlp-benefit--focus' : ''}`}>
              <FeatureIcon type={f.key} />
              <div className="preregister-benefit-text">
                <h3>{f.name}, {f.time}이면 돼요</h3>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const SocialProof = () => (
    <section className="feature-section">
      <div className="feature-section-inner">
        <h2 className="feature-section-title">선생님들이 직접 말해요</h2>
        <p className="feature-section-subtitle">{COMMON_SOCIAL_PROOF.userCount} 현직 교사가 사용하고 있어요</p>
        <div className="adlp-testimonials">
          {COMMON_SOCIAL_PROOF.testimonials.map((t, i) => (
            <div key={i} className="adlp-testimonial">
              <p className="adlp-testimonial-text">"{t.text}"</p>
              <div className="adlp-testimonial-author">
                <strong>{t.name}</strong>
                <span>{t.school}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="preregister-info-block" style={{ marginTop: 24 }}>
          {COMMON_SOCIAL_PROOF.badges.map((b, i) => (
            <div key={i} className="preregister-info-item"><CheckIcon /><span>{b}</span></div>
          ))}
        </div>
      </div>
    </section>
  );

  const Price = () => {
    if (!config.showPrice) return null;
    return (
      <section className="feature-section" style={{ background: '#f8f9fa' }} id="price">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">{seasonForPage.label} 특별 가격</h2>
          <p className="feature-section-subtitle">{seasonForPage.deadlineLabel} 적용되는 특별 가격이에요</p>
          <Countdown />
          <div className="preregister-plans">
            {getPricePlans().map((p, i) => (
              <div key={i} className={`preregister-plan ${p.highlight ? 'preregister-plan--active' : ''}`}>
                {p.highlight && <span className="preregister-plan-badge">가장 인기</span>}
                <h3>{p.period}</h3>
                <div className="preregister-plan-price">
                  <span className="preregister-plan-original">{p.original}원</span>
                  <span className="preregister-plan-sale">{p.sale}원</span>
                </div>
                <span className="preregister-plan-monthly">월 {p.monthly}원</span>
              </div>
            ))}
          </div>
          <div className="preregister-info-block">
            <div className="preregister-info-item"><CheckIcon /><span>결제 시기는 자유롭게 선택할 수 있어요</span></div>
            <div className="preregister-info-item"><CheckIcon /><span>학교 예산(S2B) 결제, 개인 결제 모두 가능해요</span></div>
            <div className="preregister-info-item"><CheckIcon /><span>세금계산서 발급돼요</span></div>
          </div>
        </div>
      </section>
    );
  };

  const Countdown = () => (
    <div className="adlp-countdown">
      <p className="adlp-countdown-label">마감까지 남은 시간</p>
      <div className="adlp-countdown-row">
        {[
          { v: countdown.days, u: '일' },
          { v: String(countdown.hours).padStart(2, '0'), u: '시간' },
          { v: String(countdown.minutes).padStart(2, '0'), u: '분' },
          { v: String(countdown.seconds).padStart(2, '0'), u: '초' },
        ].map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="adlp-countdown-sep">:</span>}
            <div className="adlp-countdown-item">
              <span className="adlp-countdown-num">{item.v}</span>
              <span className="adlp-countdown-unit">{item.u}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // cutover 시간 분기 (5/31까지 50%, 6/1부터 30%)
  const _prices = getNewYearPrices();
  const plans = [
    { id: '12month', label: '12개월', original: 200000, price: _prices['12month'], discount: getDiscountRateLabel('12month'), perMonth: Math.round(_prices['12month'] / 12).toLocaleString(), best: true },
    { id: '6month', label: '6개월', original: 110000, price: _prices['6month'], discount: getDiscountRateLabel('6month'), perMonth: Math.round(_prices['6month'] / 6).toLocaleString() },
    { id: '3month', label: '3개월', original: 60000, price: _prices['3month'], discount: getDiscountRateLabel('3month'), perMonth: Math.round(_prices['3month'] / 3).toLocaleString() },
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const msgParts = [`[광고랜딩 /lp/${adSlug}]`];
      if (config.adSetName) msgParts.push(`광고세트: ${config.adSetName}`);
      if (formData.message) msgParts.push(formData.message);

      const response = await fetch(`${getApiBaseUrl()}/api/user/pre-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          organization: formData.organization,
          phone: formData.phone,
          email: formData.email,
          selectedPlan: formData.selectedPlan,
          quantity: formData.quantity || '1계정',
          message: msgParts.join(' | '),
          eventType: '2026_new_semester',
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          utm_content: utmParams.utm_content || null,
          ...getMetaMatchParams(),
        })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || '신청에 실패했어요.');
      }
      setIsSubmitted(true);
      const planPricesGA = getNewYearPrices();
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'pre_register_submit', {
          event_category: 'Conversion',
          event_label: formData.selectedPlan,
          value: planPricesGA[formData.selectedPlan] || 0,
          currency: 'KRW',
          landing_page: `/lp/${adSlug}`,
          ad_set: config.adSetName,
          ...utmParams,
        });
        window.gtag('event', 'generate_lead', {
          value: planPricesGA[formData.selectedPlan] || 0,
          currency: 'KRW',
        });
      }
      if (typeof window.fbq !== 'undefined') {
        const planPrices = getNewYearPrices();
        window.fbq('track', 'Lead', {
          content_name: '시즌특가',
          content_category: formData.selectedPlan,
          value: planPrices[formData.selectedPlan] || 0,
          currency: 'KRW',
          landing_page: `/lp/${adSlug}`,
          ad_set: config.adSetName,
          ...utmParams,
        });
      }
    } catch (error) {
      showToast('신청을 접수하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const PreRegisterForm = () => {
    if (!config.hasForm) return null;
    if (isSubmitted) {
      return (
        <section className="feature-section" style={{ background: '#f8f9fa' }} id="adlp-form">
          <div className="feature-section-inner" style={{ textAlign: 'center' }}>
            <div className="preregister-success-icon" style={{ margin: '0 auto 16px' }}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 className="feature-section-title">시즌 특가 신청이 완료됐어요</h2>
            <p className="feature-section-subtitle">입력하신 이메일로 안내를 보내드릴게요.<br />결제 시기는 자유롭게 선택하실 수 있어요.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
              <button className="feature-hero-cta" onClick={() => navigate('/')}>왓퀴즈 둘러보기</button>
              <button className="feature-hero-cta" onClick={() => navigate('/auth')} style={{ background: '#f3f4f6', color: '#333d4b' }}>무료로 시작하기</button>
            </div>
          </div>
        </section>
      );
    }
    return (
      <section className="feature-section" style={{ background: '#f8f9fa' }} id="adlp-form">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">{seasonForPage.label} 신청</h2>
          <p className="feature-section-subtitle">{seasonForPage.deadlineLabel} 적용되는 특별 가격이에요</p>

          <div className="preregister-plans">
            {plans.map(plan => (
              <div
                key={plan.id}
                className={`preregister-plan ${formData.selectedPlan === plan.id ? 'preregister-plan--active' : ''}`}
                onClick={() => setFormData({ ...formData, selectedPlan: plan.id })}
              >
                {plan.best && <span className="preregister-plan-badge">가장 인기</span>}
                <h3>{plan.label}</h3>
                <div className="preregister-plan-price">
                  <span className="preregister-plan-original">{plan.original.toLocaleString()}원</span>
                  <span className="preregister-plan-sale">{plan.price.toLocaleString()}원</span>
                </div>
                <span className="preregister-plan-monthly">월 {plan.perMonth}원</span>
              </div>
            ))}
          </div>

          <div className="preregister-info-block">
            <div className="preregister-info-item"><CheckIcon /><span>결제 시기는 자유롭게 선택할 수 있어요</span></div>
            <div className="preregister-info-item"><CheckIcon /><span>학교 예산(S2B) 결제, 개인 결제 모두 가능해요</span></div>
            <div className="preregister-info-item"><CheckIcon /><span>세금계산서 발급돼요</span></div>
            <div className="preregister-info-item"><CheckIcon /><span>에듀집 등록 완료, 학운위 심의 없이 품의만 하면 돼요</span></div>
            <div className="preregister-info-item"><CheckIcon /><span>대량 도입(학년/학교 단위)도 가능해요</span></div>
            <div className="preregister-info-steps">신청 → 안내 메일 발송 → 결제 → 쿠폰 발급</div>
          </div>

          <form className="preregister-form" onSubmit={handleFormSubmit}>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>성함 (직책)</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="김왓퀴 (교사)" />
              </div>
              <div className="preregister-form-field">
                <label>소속</label>
                <input type="text" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} required placeholder="왓퀴즈초등학교" />
              </div>
            </div>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>이메일</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="안내 메일을 받을 주소" />
              </div>
              <div className="preregister-form-field">
                <label>연락처</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required placeholder="010-0000-0000" />
              </div>
            </div>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>구매 예상 개수</label>
                <input type="text" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="1계정 (학년 단위 도입 시 별도 안내)" />
              </div>
              <div className="preregister-form-field">
                <label>문의사항</label>
                <input type="text" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="궁금하신 점이 있다면 남겨주세요" />
              </div>
            </div>
            <button type="submit" className="preregister-submit" disabled={isSubmitting}>
              {isSubmitting ? '신청 중...' : '시즌 특가 신청하기'}
            </button>
            <p className="preregister-form-note">신청 후 안내 메일을 보내드려요. 결제 시기는 자유롭게 선택할 수 있어요.</p>
          </form>
        </div>
      </section>
    );
  };

  const BottomCTA = () => (
    <section className="feature-section adlp-bottom-cta">
      <div className="feature-section-inner" style={{ textAlign: 'center' }}>
        <h2 className="feature-section-title">{config.hero.headline} <em style={{ color: '#3182f6', fontStyle: 'normal' }}>{config.hero.highlight}</em></h2>
        <button className="feature-hero-cta" onClick={handleCTA} style={{ marginTop: 16 }}>{config.cta.text}</button>
        {config.cta.sub && <p className="preregister-hero-sub">{config.cta.sub}</p>}
      </div>
    </section>
  );

  // ── Template-specific sections ──

  const UrgencyBar = () => {
    if (!config.urgencyText) return null;
    return <div className="preregister-urgency-bar">시즌 특가 마감 <strong>D-{daysLeft}</strong> {config.urgencyText}</div>;
  };

  const Story = () => {
    if (!config.story) return null;
    return (
      <section className="feature-section" style={{ background: '#f8f9fa' }}>
        <div className="feature-section-inner adlp-story">
          <h2 className="feature-section-title">{config.story.title}</h2>
          <div className="adlp-story-quotes">
            {config.story.quotes.map((q, i) => (
              <p key={i} className="adlp-story-quote">
                {q.split('\n').map((l, j) => <React.Fragment key={j}>{l}{j < q.split('\n').length - 1 && <br />}</React.Fragment>)}
              </p>
            ))}
          </div>
          {config.story.conclusion && (
            <div className="adlp-story-conclusion">
              {config.story.conclusion.split('\n').map((l, i) => <p key={i}>{l}</p>)}
            </div>
          )}
        </div>
      </section>
    );
  };

  const CompareTable = () => {
    if (!config.compare) return null;
    const { competitors, whatquizPrice, rows } = config.compare;
    return (
      <section className="feature-section" style={{ background: '#f8f9fa' }}>
        <div className="feature-section-inner">
          <h2 className="feature-section-title">왜 왓퀴즈인가요?</h2>
          <div className="adlp-compare-wrap">
            <table className="adlp-compare">
              <thead>
                <tr>
                  <th>기능</th>
                  {competitors.map((c, i) => <th key={i}>{c.name}</th>)}
                  <th className="adlp-compare-hl">왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>월 비용</td>
                  {competitors.map((c, i) => <td key={i}>{c.price}</td>)}
                  <td className="adlp-compare-hl"><strong>{whatquizPrice}</strong></td>
                </tr>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.label}</td>
                    {r.values.map((v, j) => <td key={j} className="adlp-compare-x">{v ? v : '—'}</td>)}
                    <td className="adlp-compare-hl">{r.whatquiz}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  };

  const TimeCalc = () => {
    if (!config.timeCalc) return null;
    const { before, after, saved } = config.timeCalc;
    return (
      <section className="feature-section" style={{ background: '#f8f9fa' }}>
        <div className="feature-section-inner">
          <h2 className="feature-section-title">시간 절약 계산기</h2>
          <div className="adlp-timecalc">
            <div className="adlp-timecalc-row">
              <span>{before.label}</span>
              <strong className="adlp-timecalc-before">약 {before.hours}시간/년</strong>
            </div>
            <div className="adlp-timecalc-row">
              <span>{after.label}</span>
              <strong className="adlp-timecalc-after">약 {after.hours}시간/년</strong>
            </div>
            <div className="adlp-timecalc-divider" />
            <div className="adlp-timecalc-row">
              <span>{saved.label}</span>
              <strong className="adlp-timecalc-saved">{saved.hours}시간 = {saved.days}일</strong>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const CouponBanner = () => {
    if (!config.couponCode) return null;
    return (
      <div className="adlp-coupon">
        <span>쿠폰코드</span>
        <strong className="adlp-coupon-code">{config.couponCode}</strong>
        <span>입력하면 14일 무료</span>
      </div>
    );
  };

  const Gallery = () => (
    <section className="feature-section" style={{ background: '#f8f9fa' }}>
      <div className="feature-section-inner">
        <h2 className="feature-section-title">선생님들이 만든 작품</h2>
        <p className="feature-section-subtitle">교실 게시판에 붙여도, 카톡 프사로 써도, 단체방에 공유해도</p>
        <div className="adlp-gallery-grid">
          {CLASS_PHOTO_STYLE_PRESETS.map((style) => (
            <figure key={style.id} className="adlp-gallery-item">
              <img src={style.thumbnail} alt={`${style.name} 스타일 학급 사진 예시`} loading="lazy" />
              <figcaption>
                {style.name}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );

  const ProductDemo = () => {
    const demo = PRODUCT_DEMOS[config.demo];
    if (!demo) return null;
    const dType = config.demo;
    const { Demo } = demo;

    return (
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">{demo.title}</h2>
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            <Demo surface="ad_landing" />
            <div className="adlp-demo-try">
              <button className="adlp-demo-try-btn" onClick={() => {
                const dest = demo.path;
                trackDemoCta('ad_landing', dType, dest);
                if (typeof window.fbq === 'function') {
                  window.fbq('track', 'Lead', {
                    content_name: `demo_try_${dType}`,
                    content_category: 'demo_cta',
                    value: 0,
                    currency: 'KRW',
                    landing_page: `/lp/${adSlug}`,
                    ...utmParams,
                  });
                }
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'demo_cta_click', {
                    ad_set: config.adSetName,
                    demo_type: dType,
                    landing_slug: adSlug,
                    ...utmParams,
                  });
                }
                const sep = dest.includes('?') ? '&' : '?';
                const qs = Object.entries(utmParams).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
                navigate(qs ? `${dest}${sep}${qs}` : dest);
              }}>{demo.cta} →</button>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // ── Template rendering ──

  const templates = {
    'feature-demo': <>{Hero()}{ProductDemo()}{Steps()}{SocialProof()}{Features()}{BottomCTA()}</>,
    'class-photo': <>{Hero()}{Gallery()}{Features()}{SocialProof()}{BottomCTA()}</>,
    'urgency-deal': <>{UrgencyBar()}{Hero()}{ProductDemo()}{SocialProof()}{Price()}{PreRegisterForm()}{BottomCTA()}</>,
    'emotional': <>{Hero()}{Story()}{ProductDemo()}{SocialProof()}{Price()}{PreRegisterForm()}{BottomCTA()}</>,
    'comparison': <>{CouponBanner()}{Hero()}{CompareTable()}{TimeCalc()}{ProductDemo()}{SocialProof()}{Price()}{PreRegisterForm()}{BottomCTA()}</>,
  };

  return (
    <div className="feature-landing">
      <Helmet>
        <title>{config.hero.headline} {config.hero.highlight} | 왓퀴즈</title>
        <meta name="description" content={config.hero.sub.replace(/\n/g, ' ')} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {templates[config.template] || templates['feature-demo']}
      <Footer />
    </div>
  );
};

export default AdLandingPage;
